import { useState } from 'react';
import { Card, Col, Nav, Row } from 'react-bootstrap';
import DomainAuthoritySpamScore from '@/app/Serp/components/results/DomainAuthoritySpamScore';
import DomainAuthorityHistory from '@/app/Serp/components/results/DomainAuthorityHistory';
import SpamScore from '@/app/Serp/components/results/SpamScore';
import type { CompetitorDomainMetricsData } from '@/app/KeywordTracker/types/competitorDomainMetricsTypes';
import type { SerpAnalysisDomains } from '@/app/Serp/types/serpApiTypes';
import CardBarChartLoading from '@/app/common/components/skeleton/CardBarChartLoading';
import DataLoadError from '@/components/errors/DataLoadError';
import {
  isNetworkApiError,
  shouldShowDataLoadError
} from '@/app/common/utils/apiErrorUtils';
import Flex from '@/components/common/Flex';
import HelpTooltip from '@/app/common/components/HelpTooltip';
import { serpTooltips } from '@/app/Serp/tooltip/serpTooltips';

type DomainMetricsTabKey =
  | 'domain_authority'
  | 'spam_authority_visits'
  | 'spam_score';

type CompetitorDomainMetricsChartsProps = {
  data?: CompetitorDomainMetricsData;
  isFetching?: boolean;
  isError?: boolean;
  error?: unknown;
  onRetry?: () => void;
};

const tabs: {
  key: DomainMetricsTabKey;
  label: string;
  tooltip: string;
}[] = [
  {
    key: 'domain_authority',
    label: 'تاریخچه دامین آتوریتی',
    tooltip: serpTooltips.domainAuthority
  },
  {
    key: 'spam_authority_visits',
    label: 'اسپم اسکور و دامین آتوریتی و بازدید',
    tooltip: serpTooltips.spamAuthorityVisit
  },
  {
    key: 'spam_score',
    label: 'اسپم اسکور',
    tooltip: serpTooltips.spamScore
  }
];

const CompetitorDomainMetricsCharts = ({
  data,
  isFetching = false,
  isError = false,
  error,
  onRetry
}: CompetitorDomainMetricsChartsProps) => {
  const [activeTab, setActiveTab] =
    useState<DomainMetricsTabKey>('domain_authority');

  if (shouldShowDataLoadError(isError, error)) {
    return (
      <DataLoadError
        error={error}
        onRetry={onRetry}
        isRetrying={isFetching}
        isNetworkError={isNetworkApiError(error)}
      />
    );
  }

  if (isFetching || !data) {
    return (
      <Row className="g-3 mb-3">
        <Col xs={12}>
          <CardBarChartLoading
            title="تاریخچه دامین آتوریتی"
            limit={13}
            width={20}
            height={160}
            margin={20}
            marginBetweenLine={8}
            showFooter={false}
          />
        </Col>
      </Row>
    );
  }

  return (
    <Row className="g-3 mb-3">
      <Col xs={12}>
        <Card className="mb-0 overflow-hidden">
          <Card.Header
            style={{ zIndex: 1 }}
            as={Flex}
            justifyContent="between"
            alignItems="center"
            className="ps-0 py-0 position-relative border-bottom"
          >
            <Nav
              as="ul"
              className="nav-tabs border-0 flex-wrap chart-tab tab-active-caret"
            >
              {tabs.map(tab => (
                <Nav.Item key={tab.key} className="z-3" as="li">
                  <Nav.Link
                    onClick={() => setActiveTab(tab.key)}
                    className="mb-0 text-nowrap d-flex gap-1 align-items-center navLinkForceColor py-3 cursor-pointer"
                    active={activeTab === tab.key}
                  >
                    <span
                      className={
                        activeTab === tab.key ? 'fw-bold' : 'fw-normal'
                      }
                    >
                      {tab.label}
                    </span>
                    <HelpTooltip html={tab.tooltip} />
                  </Nav.Link>
                </Nav.Item>
              ))}
            </Nav>
          </Card.Header>

          <Card.Body className="pt-3">
            {activeTab === 'domain_authority' && (
              <DomainAuthorityHistory
                embedded
                data={data.domains_moz as SerpAnalysisDomains['domains_moz']}
              />
            )}
            {activeTab === 'spam_authority_visits' && (
              <DomainAuthoritySpamScore
                embedded
                data={
                  data.spam_authority_visits as SerpAnalysisDomains['spam_authority_visits']
                }
              />
            )}
            {activeTab === 'spam_score' && (
              <SpamScore embedded data={data.domains_spam_score} />
            )}
          </Card.Body>
        </Card>
      </Col>
    </Row>
  );
};

export default CompetitorDomainMetricsCharts;
